// Test data for drops v2 (api/dropGroups.js), until feature/drop-groups is
// deployed: in memory, per brand, for this page load only. The shapes are the
// contract's (portal-onboarding-drops.md §4 DropGroup, §3b edit answer).
import { embedMockAllowed } from './embed';

const store = new Map(); // brandId → DropGroup[]
let seq = 0;

export function mockAllowed() {
  return embedMockAllowed();
}

export function resetDropGroupsMock() {
  store.clear();
  seq = 0;
}

const clone = (x) => JSON.parse(JSON.stringify(x));
const listFor = (brandId) => {
  if (!store.has(brandId)) store.set(brandId, []);
  return store.get(brandId);
};

function productRow(p, dropId) {
  const images = (p.images || []).map((i) => (typeof i === 'string' ? i : i.src)).filter(Boolean);
  return {
    drop_id: dropId,
    product_id: p.id,
    product_key: p.id,
    name: p.name,
    description: p.ai_description || p.original_description || null,
    images,
    price: p.price_data?.price ?? null,
    currency: p.price_data?.currency ?? null,
    status: 'scheduled',
    product_status: p.status || 'DRAFT',
    source: p.source || 'portal_manual',
    purchasable: false,
    stock_left: null,
  };
}

function status(g, now = Date.now()) {
  if (g.status === 'cancelled') return 'cancelled';
  const start = new Date(g.starts_at).getTime();
  const end = g.ends_at ? new Date(g.ends_at).getTime() : start + 24 * 3600 * 1000;
  if (now < start) return 'scheduled';
  return now < end ? 'active' : 'completed';
}

function fail(code, statusCode = 400, extra = {}) {
  const err = new Error(code);
  err.status = statusCode;
  err.data = { message: { success: false, error: code, ...extra } };
  return err;
}

function rowsFor(productIds, products, oldRows = []) {
  return productIds.map((id, i) => {
    const kept = oldRows.find((r) => r.product_id === id);
    if (kept) return kept;
    const p = products.find((x) => String(x.id) === String(id));
    if (!p) throw fail('PRODUCT_NOT_FOUND', 404, { product_id: id });
    return productRow(p, `md${++seq}_${i}`);
  });
}

export function mockList(brandId) {
  return clone(listFor(brandId).map((g) => ({ ...g, status: status(g) })).sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at)));
}

export function mockCreate(brandId, body, products) {
  const id = `mg${++seq}`;
  const now = new Date().toISOString();
  const g = {
    id, brand_id: brandId, name: body.name, description: body.description ?? null,
    starts_at: body.startsAt, ends_at: null, mode: 'exclusive', exclusive: true,
    status: 'scheduled', activated_at: null, created_at: now, updated_at: now,
    products: rowsFor(body.productIds || [], products),
    subscriber_count: 0,
  };
  g.product_count = g.products.length;
  listFor(brandId).push(g);
  return clone(g);
}

function find(groupId) {
  for (const list of store.values()) {
    const g = list.find((x) => x.id === groupId);
    if (g) return { g, list };
  }
  throw fail('DROP_GROUP_NOT_FOUND', 404);
}

export function mockUpdate(groupId, body, products = []) {
  const { g } = find(groupId);
  if (body.status === 'cancelled') {
    g.status = 'cancelled';
    g.products.forEach((r) => { r.status = 'cancelled'; });
    return clone(g);
  }
  if (status(g) !== 'scheduled' && (body.startsAt || body.productIds)) throw fail('DROP_ALREADY_STARTED', 409);
  if (body.name !== undefined) g.name = body.name;
  if (body.description !== undefined) g.description = body.description;
  if (body.startsAt !== undefined) g.starts_at = body.startsAt;
  if (body.productIds) {
    g.products = rowsFor(body.productIds, products, g.products);
    g.product_count = g.products.length;
  }
  g.updated_at = new Date().toISOString();
  return clone({ ...g, status: status(g) });
}

export function mockDelete(groupId) {
  const { g, list } = find(groupId);
  list.splice(list.indexOf(g), 1);
  return { id: groupId, deleted: true };
}

/// PUT …/products/manual/:id, applied locally. New photos show as blob URLs;
/// a change to the photos, the name or the description "re-runs" the AI,
/// which here keeps its previous verdict.
export function mockEditProduct(product, changes) {
  const p = clone(product);
  let images = (p.images || []).map((i) => (typeof i === 'string' ? { src: i } : i));
  if (changes.replaceImages) images = [];
  if (changes.removeImages?.length) images = images.filter((i) => !changes.removeImages.includes(i.src));
  (changes.images || []).forEach((f) => images.push({ src: URL.createObjectURL(f) }));
  p.images = images.map((i, n) => ({ ...i, position: n + 1 }));
  if (changes.name !== undefined) p.name = changes.name;
  if (changes.description !== undefined) p.original_description = changes.description;
  if (changes.price !== undefined || changes.currency !== undefined) {
    p.price_data = {
      price: changes.price !== undefined ? Number(String(changes.price).replace(',', '.')).toFixed(2) : p.price_data?.price,
      currency: changes.currency ?? p.price_data?.currency,
    };
  }
  if (changes.sizes !== undefined) {
    p.variants = changes.sizes.map((s, i) => ({ id: `${p.id}:${i + 1}`, title: s, option1: s, price: p.price_data?.price, inventory_quantity: null }));
  }
  const reanalyzed = !!(changes.images?.length || changes.removeImages?.length || changes.replaceImages || changes.name !== undefined || changes.description !== undefined);
  const usable = p.is_clothing === true;
  return {
    product: p,
    labels: null,
    reanalyzed,
    isClothing: p.is_clothing === true,
    usable,
    reason: usable ? null : p.is_clothing === false ? 'NOT_CLOTHING' : 'LABELING_PENDING',
    affectedDrops: { updated: [], removed: [] },
    mock: true,
  };
}
